import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  DashboardTabs,
  TaskSearchBar,
  TaskList,
  InProgressBottomSheet,
  InProgressTaskDetailModal,
  CompleteTaskDialog,
  CancelConfirmDialog,
  ViewActionDialog,
  CompletedInsightsBar,
  RefreshBubble,
  NotificationBadge,
  YmsAlertDialog,
} from '@/components/dashboard';
import { NotificationPopupDialog } from '@/components/notification';
import { useToast } from '@/components/ui';
import { PRIORITY_CONFIG, ROUTES } from '@/constants';
import {
  useAutoRefresh,
  useElapsedTimer,
  useOfflineSync,
  useUpcomingTasksQuery,
  useMyTasksQuery,
  useCompletedTasksQuery,
  useInProgressTaskQuery,
  useNotificationCountQuery,
  useAcceptTaskMutation,
  useStartTaskMutation,
  useCancelTaskMutation,
  dashboardQueryKeys,
} from '@/hooks/dashboard';
import { usePreventBackNavigation } from '@/hooks/usePreventBackNavigation';
import { useAppSelector } from '@/store';
import { clearUnreadNotificationIds, isTaskInCurrentSession, validateAcceptTask } from '@/utils';

import type { TaskDetail, TaskTabType, CompleteTaskFlow, QuestionFlowNode } from '@/types';

// ─────────────────────────────────────────────────────────────────

// Stable empty reference to avoid creating new arrays each render
const EMPTY_TASKS: TaskDetail[] = [];

/**
 * Parse the COMPLETE_TASK_FLOW config setting from a JSON string.
 */
function parseCompleteTaskFlow(
  settings: Record<string, string | number | boolean> | null,
  language: string
): CompleteTaskFlow | null {
  if (language === 'es' && settings?.['COMPLETE_TASK_FLOW_SPANISH']) {
    try {
      return JSON.parse(String(settings['COMPLETE_TASK_FLOW_SPANISH'])) as CompleteTaskFlow;
    } catch {
      // Fall through to English
    }
  }
  if (!settings?.['COMPLETE_TASK_FLOW']) return null;
  try {
    return JSON.parse(String(settings['COMPLETE_TASK_FLOW'])) as CompleteTaskFlow;
  } catch {
    return null;
  }
}

/**
 * Get the correct question flow root node based on task type.
 */
function getFlowRootForTask(
  task: TaskDetail,
  flow: CompleteTaskFlow | null
): QuestionFlowNode | null {
  if (!flow) return null;

  if (task.TaskType === 'Move') {
    return flow.move;
  }

  // For Audit, determine if there's a trailer
  if (task.TrailerName) {
    return flow.audit;
  }
  return flow.audit_without_trailer;
}

/**
 * Filter tasks based on active tab, task filter, and search query.
 * Matches Xamarin ApplyTaskListFilter logic.
 */
function filterTasks(
  tasks: TaskDetail[],
  tab: TaskTabType,
  taskFilter: string,
  searchQuery: string
): TaskDetail[] {
  let filtered = [...tasks];

  // On upcoming tab, sort critical tasks first (matches Xamarin)
  if (tab === 'upcoming') {
    filtered.sort((a, b) => {
      const aIsCritical =
        a.Priority >= PRIORITY_CONFIG.CRITICAL.min && a.Priority <= PRIORITY_CONFIG.CRITICAL.max;
      const bIsCritical =
        b.Priority >= PRIORITY_CONFIG.CRITICAL.min && b.Priority <= PRIORITY_CONFIG.CRITICAL.max;
      if (aIsCritical && !bIsCritical) return -1;
      if (!aIsCritical && bIsCritical) return 1;
      return 0;
    });
  }

  // Filter by task type
  if (taskFilter === 'move') {
    filtered = filtered.filter((t) => t.TaskType === 'Move');
  } else if (taskFilter === 'audit') {
    filtered = filtered.filter((t) => t.TaskType === 'Audit');
  }

  // Filter by search query
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (t) =>
        t.TrailerName.toLowerCase().includes(q) ||
        t.MoveSourceName.toLowerCase().includes(q) ||
        t.MoveDestinationName.toLowerCase().includes(q) ||
        t.AuditLocationName.toLowerCase().includes(q) ||
        t.TaskId.toLowerCase().includes(q)
    );
  }

  return filtered;
}

export function DashboardPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Auth & app state
  const { plant } = useAppSelector((s) => s.user);
  const settings = useAppSelector((s) => s.global.settings);

  // ── Back navigation prevention — matches Xamarin DashboardPage.OnBackButtonPressed ──
  const [showExitDialog, setShowExitDialog] = useState(false);
  const allowBackNavigationRef = useRef(false);

  const handleBackAttempt = useCallback(() => {
    setShowExitDialog(true);
  }, []);

  usePreventBackNavigation(true, allowBackNavigationRef, handleBackAttempt);

  // ── TanStack Query hooks ──
  const upcomingQuery = useUpcomingTasksQuery();
  const myTasksQuery = useMyTasksQuery();
  const completedQuery = useCompletedTasksQuery();
  const inProgressQuery = useInProgressTaskQuery();
  const notificationCountQuery = useNotificationCountQuery();

  // ── In-progress timer — matches Xamarin Timer_Elapsed (recalculates from TaskStartDateTime) ──
  const inProgressTask = inProgressQuery.data ?? null;
  const elapsedSeconds = useElapsedTimer(inProgressTask);

  // ── Mutation hooks ──
  const acceptMutation = useAcceptTaskMutation();
  const startMutation = useStartTaskMutation();
  const cancelMutation = useCancelTaskMutation();

  // ── Local UI state (previously in Redux) ──
  const locationState = location.state as { tab?: TaskTabType } | null;
  const [activeTab, setActiveTab] = useState<TaskTabType>(locationState?.tab ?? 'upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [taskFilter, setTaskFilter] = useState<'all' | 'audit' | 'move'>('all');
  const [showRefreshBubble, setShowRefreshBubble] = useState(false);

  // Task operation UI state
  const [operationError, setOperationError] = useState<string | null>(null);
  const [operatingTaskId, setOperatingTaskId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogTaskId, setConfirmDialogTaskId] = useState<string | null>(null);

  // Complete flow state
  const [completeFlowActive, setCompleteFlowActive] = useState(false);
  const [completeFlowTask, setCompleteFlowTask] = useState<TaskDetail | null>(null);
  const [completeFlowRootStep, setCompleteFlowRootStep] = useState<QuestionFlowNode | null>(null);

  // Notification popup state — Matches Xamarin OpenNotificationPopUpCommand
  const [notificationPopupOpen, setNotificationPopupOpen] = useState(false);

  // ViewAction dialog state (task to show detail popup for)
  const [viewActionTask, setViewActionTask] = useState<TaskDetail | null>(null);

  // In-progress task detail modal state (separate from ViewActionDialog)
  const [inProgressDetailOpen, setInProgressDetailOpen] = useState(false);

  // Alert dialog state — matches Xamarin AlertDialog (navy header + message + Ok button)
  const [alertDialog, setAlertDialog] = useState<{
    title: string;
    message: string;
    showCancel: boolean;
    onOk: () => void;
  } | null>(null);

  // Toast hook — matches Xamarin ToastMessageDialog (bottom toast with auto-dismiss)
  const { showToast } = useToast();

  // Offline sync — pushes queued task updates when network returns
  // Matches Xamarin Connectivity_ConnectivityChanged + PushDataToServer
  useOfflineSync(showToast);

  // ── Derived data ──
  const upcomingTasks = upcomingQuery.data ?? EMPTY_TASKS;
  const myTasks = myTasksQuery.data ?? EMPTY_TASKS;
  const completedTasks = completedQuery.data ?? EMPTY_TASKS;

  // Track data changes — show spinner only when task list actually updates
  const [showDataRefresh, setShowDataRefresh] = useState(false);
  const prevUpcomingLenRef = useRef<number | null>(null);
  const prevCompletedLenRef = useRef<number | null>(null);

  useEffect(() => {
    const len = upcomingTasks.length;
    const changed = prevUpcomingLenRef.current !== null && prevUpcomingLenRef.current !== len;
    prevUpcomingLenRef.current = len;
    if (!changed) return;
    setShowDataRefresh(true);
    const timer = setTimeout(() => {
      setShowDataRefresh(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [upcomingTasks]);

  useEffect(() => {
    const len = completedTasks.length;
    const changed = prevCompletedLenRef.current !== null && prevCompletedLenRef.current !== len;
    prevCompletedLenRef.current = len;
    if (!changed) return;
    setShowDataRefresh(true);
    const timer = setTimeout(() => {
      setShowDataRefresh(false);
    }, 1500);
    return () => {
      clearTimeout(timer);
    };
  }, [completedTasks]);

  // Session-scoped completed tasks — matches Xamarin ApplyTaskListFilter() case 2:
  // Show tasks whose LastUpdatedDatetime falls within the current day (PST)
  const sessionCompletedTasks = useMemo(() => {
    return completedTasks.filter((task) => isTaskInCurrentSession(task.LastUpdatedDatetime));
  }, [completedTasks]);

  const unreadNotificationCount = notificationCountQuery.data ?? 0;

  // Move/Audit counts for My Tasks tab badges (matches Xamarin DashboardPage TabHostView)
  const moveTaskCount = myTasks.filter((t) => t.TaskType === 'Move').length;
  const auditTaskCount = myTasks.filter((t) => t.TaskType === 'Audit').length;

  // Acceptance criteria from plant config
  const moveLimit = plant?.move_task_acceptance_criteria ?? 5;
  const auditLimit = plant?.audit_task_acceptance_criteria ?? 5;

  // Parse complete task flow from settings
  const completeTaskFlow = parseCompleteTaskFlow(settings, i18n.language);

  // Get current tab's tasks and loading status
  const currentTabTasks = useMemo(() => {
    switch (activeTab) {
      case 'upcoming':
        return upcomingTasks;
      case 'myTasks':
        return myTasks;
      case 'completed':
        return sessionCompletedTasks;
    }
  }, [activeTab, upcomingTasks, myTasks, sessionCompletedTasks]);
  const tabStatus = useMemo(() => {
    switch (activeTab) {
      case 'upcoming':
        return upcomingQuery.isLoading ? 'loading' : upcomingQuery.isError ? 'error' : 'success';
      case 'myTasks':
        return myTasksQuery.isLoading ? 'loading' : myTasksQuery.isError ? 'error' : 'success';
      case 'completed':
        return completedQuery.isLoading ? 'loading' : completedQuery.isError ? 'error' : 'success';
    }
  }, [
    activeTab,
    upcomingQuery.isLoading,
    upcomingQuery.isError,
    myTasksQuery.isLoading,
    myTasksQuery.isError,
    completedQuery.isLoading,
    completedQuery.isError,
  ]);

  const filteredTasks = useMemo(
    () => filterTasks(currentTabTasks, activeTab, taskFilter, searchQuery),
    [currentTabTasks, activeTab, taskFilter, searchQuery]
  );

  // Xamarin: SearchCommandHandler uses SearchEmptyMessage when search yields no results;
  // ApplyEmptyMessage uses EmptyFilterBody when filter yields no results but tasks exist.
  const emptyReason = useMemo(() => {
    if (filteredTasks.length > 0 || currentTabTasks.length === 0) return 'noTasks' as const;
    if (searchQuery.trim()) return 'search' as const;
    if (taskFilter !== 'all') return 'filter' as const;
    return 'noTasks' as const;
  }, [filteredTasks.length, currentTabTasks.length, searchQuery, taskFilter]);

  // Operation status derived from mutations
  const operationStatus = useMemo(() => {
    if (acceptMutation.isPending) return 'accepting';
    if (startMutation.isPending) return 'starting';
    if (cancelMutation.isPending) return 'cancelling';
    return 'idle';
  }, [acceptMutation.isPending, startMutation.isPending, cancelMutation.isPending]);

  // ── Refetch helper for current tab ──
  const refetchCurrentTab = useCallback(() => {
    switch (activeTab) {
      case 'upcoming':
        void upcomingQuery.refetch();
        break;
      case 'myTasks':
        void myTasksQuery.refetch();
        break;
      case 'completed':
        void completedQuery.refetch();
        break;
    }
  }, [activeTab, upcomingQuery, myTasksQuery, completedQuery]);

  // ── Auto-refresh integration ──
  useAutoRefresh(refetchCurrentTab, activeTab, () => {
    setShowRefreshBubble(true);
  });

  // ── Tab change handler ──
  const handleTabChange = useCallback((tab: TaskTabType) => {
    setActiveTab(tab);
    // Refetch is handled automatically by TanStack Query when data is stale
  }, []);

  // ── Refresh bubble handler ──
  const handleRefresh = useCallback(() => {
    setShowRefreshBubble(false);
    void upcomingQuery.refetch();
  }, [upcomingQuery]);

  // ── Accept task ──
  // Xamarin: AcceptTask() → ValidateCriticalAndHighPriority → DisplayAlertAsync (blocks)
  //          Also checks acceptance criteria limit → DisplayAlertAsync (blocks)
  const handleAccept = useCallback(
    (task: TaskDetail) => {
      // Matches Xamarin: Accept not allowed offline → ShowToast(NoInternetMessage, Warning)
      if (!navigator.onLine) {
        showToast(t('dashboard.noInternet'), { type: 'warning' });
        return;
      }

      // Validate acceptance criteria
      const warning = validateAcceptTask(task, upcomingTasks, myTasks, moveLimit, auditLimit);
      if (warning) {
        // Xamarin: Acceptance limit → AlertDialog "Maximum limit reached" / "Please complete already accepted task."
        if (warning.includes('can only accept')) {
          setAlertDialog({
            title: t('dashboard.maxLimitReached'),
            message: t('dashboard.completeAcceptedTask'),
            showCancel: false,
            onOk: () => {
              setAlertDialog(null);
            },
          });
          return;
        }
        // Xamarin: Priority validation → DisplayAlertAsync (BLOCKS acceptance, no override)
        const isCritical = warning.includes('Critical');
        const isAudit = task.TaskType === 'Audit';
        setAlertDialog({
          title: isCritical ? t('dashboard.criticalTasksPending') : t('dashboard.highTasksPending'),
          message: isCritical
            ? isAudit
              ? t('dashboard.completeCriticalAudit')
              : t('dashboard.completeCriticalMove')
            : isAudit
              ? t('dashboard.completeHighAudit')
              : t('dashboard.completeHighMove'),
          showCancel: false,
          onOk: () => {
            setAlertDialog(null);
          },
        });
        return;
      }

      setOperatingTaskId(task.TaskId);
      acceptMutation.mutate(task, {
        onSuccess: () => {
          showToast(t('dashboard.taskAccepted'), { type: 'success' });
          setOperatingTaskId(null);
        },
        onError: (error) => {
          setOperationError(error instanceof Error ? error.message : t('dashboard.failedToAccept'));
          setOperatingTaskId(null);
        },
      });
    },
    [acceptMutation, upcomingTasks, myTasks, moveLimit, auditLimit, showToast, t]
  );

  // ── Start task from CTA button — different behavior per tab ──
  // My Tasks tab: AcceptOrStartCommand → StartTask (directly starts)
  // Upcoming tab: StartCommand → StartTask (directly starts)
  // Xamarin: StartTask() → ShowToast(StartTaskErrorMessage, Warning) if in-progress exists
  //          On success → ShowToast(StartSuccess, Success)
  const handleStartFromCTA = useCallback(
    (task: TaskDetail) => {
      // Matches Xamarin: Start from Upcoming tab not allowed offline → ShowToast(NoInternetMessage, Warning)
      if (!navigator.onLine && activeTab === 'upcoming') {
        showToast(t('dashboard.noInternet'), { type: 'warning' });
        return;
      }

      if (inProgressTask) {
        // Xamarin: ShowToast("Another task is in progress.", ToastType.Warning) → red #Ff4a4a
        showToast(t('dashboard.anotherTaskInProgress'), { type: 'warning' });
        return;
      }

      setOperatingTaskId(task.TaskId);
      startMutation.mutate(task, {
        onSuccess: () => {
          showToast(t('dashboard.taskStarted'), { type: 'success' });
          setOperatingTaskId(null);
        },
        onError: (error) => {
          setOperationError(error instanceof Error ? error.message : t('dashboard.failedToStart'));
          setOperatingTaskId(null);
        },
      });
    },
    [startMutation, inProgressTask, activeTab, showToast, t]
  );

  // ── Start task from ViewAction dialog ──
  const handleStartFromDialog = useCallback(() => {
    if (!viewActionTask) return;

    // Matches Xamarin: Start from Upcoming tab not allowed offline → ShowToast(NoInternetMessage, Warning)
    if (!navigator.onLine && activeTab === 'upcoming') {
      showToast(t('dashboard.noInternet'), { type: 'warning' });
      return;
    }

    if (inProgressTask) {
      // Xamarin: ShowToast("Another task is in progress.", ToastType.Warning)
      showToast(t('dashboard.anotherTaskInProgress'), { type: 'warning' });
      return;
    }

    setOperatingTaskId(viewActionTask.TaskId);
    startMutation.mutate(viewActionTask, {
      onSuccess: () => {
        setViewActionTask(null);
        showToast(t('dashboard.taskStarted'), { type: 'success' });
        setOperatingTaskId(null);
      },
      onError: (error) => {
        setOperationError(error instanceof Error ? error.message : t('dashboard.failedToStart'));
        setOperatingTaskId(null);
      },
    });
  }, [startMutation, viewActionTask, inProgressTask, activeTab, showToast, t]);

  // ── Accept task from ViewAction dialog ──
  const handleAcceptFromDialog = useCallback(() => {
    if (!viewActionTask) return;
    handleAccept(viewActionTask);
    setViewActionTask(null);
  }, [viewActionTask, handleAccept]);

  // ── Cancel task request (opens confirm dialog) ──
  const handleCancelRequest = useCallback((task: TaskDetail) => {
    setConfirmDialogOpen(true);
    setConfirmDialogTaskId(task.TaskId);
  }, []);

  // ── Cancel task confirm ──
  const handleCancelConfirm = useCallback(() => {
    // Matches Xamarin: Cancel not allowed offline → ShowToast(NoInternetMessage, Warning)
    if (!navigator.onLine) {
      showToast(t('dashboard.noInternet'), { type: 'warning' });
      setConfirmDialogOpen(false);
      setConfirmDialogTaskId(null);
      return;
    }

    const taskId = confirmDialogTaskId;
    if (!taskId) return;

    // Check if it's the in-progress task
    const taskToCancel =
      inProgressTask?.TaskId === taskId ? inProgressTask : myTasks.find((t) => t.TaskId === taskId);

    if (!taskToCancel) return;

    setOperatingTaskId(taskId);
    cancelMutation.mutate(taskToCancel, {
      onSuccess: () => {
        setConfirmDialogOpen(false);
        setConfirmDialogTaskId(null);
        showToast(t('dashboard.taskReleased'), { type: 'success' });
        setOperatingTaskId(null);
      },
      onError: (error) => {
        setOperationError(error instanceof Error ? error.message : t('dashboard.failedToCancel'));
        setOperatingTaskId(null);
      },
    });
  }, [cancelMutation, confirmDialogTaskId, inProgressTask, myTasks, showToast, t]);

  // ── Complete task request (opens question flow) ──
  const handleCompleteRequest = useCallback(
    (task: TaskDetail) => {
      const rootStep = getFlowRootForTask(task, completeTaskFlow);
      if (rootStep) {
        setCompleteFlowTask(task);
        setCompleteFlowRootStep(rootStep);
        setCompleteFlowActive(true);
      } else {
        // No flow configured — show alert dialog
        setAlertDialog({
          title: t('common.error'),
          message: t('dashboard.errorFlowNotConfigured'),
          showCancel: false,
          onOk: () => {
            setAlertDialog(null);
          },
        });
      }
    },
    [completeTaskFlow, t]
  );

  // ── Notification popup — Matches Xamarin OpenNotificationPopUpCommand ──
  // Resets bell badge and fetches fresh notification list from API
  const handleOpenNotificationPopup = useCallback(() => {
    // Matches Xamarin UnreadNotificationTaskIdList.Clear() — reset bell badge to 0
    clearUnreadNotificationIds();
    queryClient.setQueryData<number>(
      dashboardQueryKeys.notificationCount(String(plant?.org_id ?? '')),
      0
    );
    // Matches Xamarin Notification(limit, selectedFilter) — clear cache so popup
    // shows loading state, then fetches fresh data from API
    queryClient.removeQueries({
      queryKey: ['dashboard', 'notificationList'],
    });
    setNotificationPopupOpen(true);
  }, [queryClient, plant?.org_id]);

  const handleCloseNotificationPopup = useCallback(() => {
    setNotificationPopupOpen(false);
  }, []);

  // ── View task — opens ViewAction popup (matches Xamarin: card tap → ViewActionCommand on ALL tabs) ──
  const handleViewAction = useCallback((task: TaskDetail) => {
    setViewActionTask(task);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Error banner */}
      {operationError && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>{operationError}</span>
          </div>
          <button
            onClick={() => {
              setOperationError(null);
            }}
            className="text-xs text-destructive hover:underline"
          >
            {t('common.dismiss')}
          </button>
        </div>
      )}

      {/* Bottom navigation tabs */}
      <DashboardTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        moveTaskCount={moveTaskCount}
        auditTaskCount={auditTaskCount}
      />

      {/* Refresh bubble */}
      <div className="flex justify-center -mb-2 relative z-10">
        <RefreshBubble visible={showRefreshBubble} onClick={handleRefresh} />
      </div>

      {/* Search & filter — Xamarin: SearchBarAndFilter Margin=0, Padding=0 (inner frames handle margins) */}
      <div className="pt-2 pb-1">
        <TaskSearchBar
          searchQuery={searchQuery}
          taskFilter={taskFilter}
          onSearchChange={setSearchQuery}
          onFilterChange={setTaskFilter}
        />
      </div>

      {/* Spinner — shown briefly when task data actually changes */}
      {showDataRefresh && (activeTab === 'upcoming' || activeTab === 'completed') && (
        <div className="flex items-center justify-center py-2">
          <Loader2 className="h-5 w-5 animate-spin text-[#1357A0]" />
        </div>
      )}

      {/* Completed tab insights bar — Xamarin: CompletedTaskInsightsBackground #7ED1DC, visible on SelectedIndex=2 */}
      {activeTab === 'completed' && <CompletedInsightsBar tasks={sessionCompletedTasks} />}

      {/* Task list – bottom padding accounts for fixed bottom nav (72px) + optional in-progress sheet */}
      <div className={`flex-1 overflow-y-auto px-4 ${inProgressTask ? 'pb-40' : 'pb-24'}`}>
        <TaskList
          tasks={filteredTasks}
          tab={activeTab}
          isLoading={tabStatus === 'loading'}
          operatingTaskId={operatingTaskId}
          hasInProgressTask={!!inProgressTask}
          emptyReason={emptyReason}
          onAccept={activeTab === 'upcoming' ? handleAccept : undefined}
          onStart={
            activeTab === 'myTasks' || activeTab === 'upcoming' ? handleStartFromCTA : undefined
          }
          onCancel={activeTab === 'myTasks' ? handleCancelRequest : undefined}
          onView={handleViewAction}
          onNavigateToTab={handleTabChange}
        />
      </div>

      {/* Notification FAB — Matches Xamarin NotificationFAB.xaml positioned in DashboardPage.xaml:
          Grid.RowSpan=4, HorizontalOptions=End, VerticalOptions=End,
          HeightRequest=50, WidthRequest=50, Padding=5,
          Margin="{OnIdiom Phone='0,0,0,130'}" (left=0, top=0, right=0, bottom=130) */}
      <div className="fixed bottom-[130px] right-4 z-50">
        <NotificationBadge count={unreadNotificationCount} onPress={handleOpenNotificationPopup} />
      </div>

      {/* Notification popup dialog — Matches Xamarin NotificationListPopUp (IsPage=false mode) */}
      <NotificationPopupDialog
        open={notificationPopupOpen}
        onClose={handleCloseNotificationPopup}
      />

      {/* In-progress bottom sheet */}
      {inProgressTask && (
        <InProgressBottomSheet
          task={inProgressTask}
          elapsedSeconds={elapsedSeconds}
          onViewAction={() => {
            setInProgressDetailOpen(true);
          }}
          onComplete={() => {
            handleCompleteRequest(inProgressTask);
          }}
        />
      )}

      {/* In-progress task detail modal — matches Xamarin DashboardPage.xaml BottomSheetControl
          Opens when tapping the in-progress bar (separate from ViewActionDialog used by other modules) */}
      {inProgressTask && inProgressDetailOpen && (
        <InProgressTaskDetailModal
          task={inProgressTask}
          onClose={() => {
            setInProgressDetailOpen(false);
          }}
          onCancel={() => {
            setInProgressDetailOpen(false);
            handleCancelRequest(inProgressTask);
          }}
          onComplete={() => {
            setInProgressDetailOpen(false);
            handleCompleteRequest(inProgressTask);
          }}
        />
      )}

      {/* Complete task dialog */}
      <CompleteTaskDialog
        active={completeFlowActive}
        task={completeFlowTask}
        rootStep={completeFlowRootStep}
        elapsedSeconds={elapsedSeconds}
        onClose={() => {
          setCompleteFlowActive(false);
          setCompleteFlowTask(null);
          setCompleteFlowRootStep(null);
        }}
      />

      {/* ViewAction task detail dialog (matches Xamarin ViewActionMobilePage)
          Buttons shown based on task status:
          - Released (20):    Accept button
          - Assigned (30):    Cancel + Start buttons
          - In-Progress (40): Cancel button only
          - Completed (60):   No buttons */}
      {viewActionTask && (
        <ViewActionDialog
          task={viewActionTask}
          onClose={() => {
            setViewActionTask(null);
          }}
          onAccept={handleAcceptFromDialog}
          onStart={handleStartFromDialog}
          onCancel={() => {
            const task = viewActionTask;
            setViewActionTask(null);
            handleCancelRequest(task);
          }}
          isLoading={operationStatus === 'starting' || operationStatus === 'accepting'}
        />
      )}

      {/* Cancel confirm dialog */}
      <CancelConfirmDialog
        open={confirmDialogOpen}
        isLoading={operationStatus === 'cancelling'}
        onConfirm={handleCancelConfirm}
        onClose={() => {
          setConfirmDialogOpen(false);
          setConfirmDialogTaskId(null);
        }}
      />

      {/* Alert dialog — matches Xamarin AlertDialog.xaml (navy header + Ok button) */}
      {alertDialog && (
        <YmsAlertDialog
          open
          title={alertDialog.title}
          message={alertDialog.message}
          showCancel={alertDialog.showCancel}
          onOk={alertDialog.onOk}
          onCancel={() => {
            setAlertDialog(null);
          }}
        />
      )}

      {/* Exit confirmation dialog — shown on back navigation */}
      <YmsAlertDialog
        open={showExitDialog}
        title={t('common.exitConfirmTitle', 'Exit Application')}
        message={t('common.exitConfirmMessage', 'Are you sure you want to exit the application?')}
        okText={t('common.yes', 'Yes')}
        cancelText={t('common.no', 'No')}
        showCancel
        onOk={() => {
          allowBackNavigationRef.current = true;
          setShowExitDialog(false);
          navigate(ROUTES.logoutSummary);
        }}
        onCancel={() => {
          setShowExitDialog(false);
        }}
      />
    </div>
  );
}
